import {
  WebGLRenderer,
  PerspectiveCamera,
  Scene,
  AmbientLight,
  DirectionalLight,
  Color,
  Clock,
  Vector3,
  Fog,
  Raycaster,
} from 'three';
import { createTilesRenderer } from './tiles.js';
import { loadAircraft, rotateModelYaw90 } from './aircraft.js';
import { createControls, createKeyboardInputProvider } from './controls.js';
import {
  isTouchDevice,
  createMobileInputProvider,
  hideOverlay,
} from './mobileControls.js';
import { updateChaseCamera } from './chaseCamera.js';
import { createHud } from './hud.js';
import { createEngineAudio } from './audio.js';
import { createMinimap } from './minimap.js';
import { createCityTracker } from './cityTracker.js';
import { createEnuFrame } from './geo.js';
import { airportsToEnu, nearestAirport, landingAirport } from './airports.js';
import { createCoastlineClassifier } from './coastline.js';
import { sitesToEnu, nearestSite, TOTAL_SITES } from './historicSites.js';
import { createHistoricRings } from './historicRings.js';
import { createSiteModal } from './siteModal.js';
import { progress } from './historicProgress.js';

const MAX_SPEED_MS = 900 / 3.6; // 陸・領海の上限 ~900 km/h（タイル streaming を抑える）
const MAX_SPEED_OCEAN_MS = 5000 / 3.6; // 公海（海岸から12海里超）は 5000 km/h まで許可
// ※ 公海判定のしきい値・ヒステリシス・スロットルは coastline.js が own する。
const SPAWN_ALTITUDE = 250; // metres above the chosen lat/lng surface
const ON_GROUND_MARGIN = 0.5; // additional margin to consider aircraft "on ground"
const EARTH_R = 6378000;    // metres (WGS84 mean)

// --- 着陸/離陸・空港接近の判定パラメータ ---
const LANDING_AGL_AIRBORNE = 30;   // この対地高度(m)を超えて初めて着陸判定をアーム（タクシー振動で誤発火しない）
const LANDING_VS_GOOD = 6;         // 接地降下率(m/s) これ以下なら良好な着陸
const LANDING_VS_HARD = 12;        // これを超える降下率は着陸失敗
const LANDING_SPD_MAX = 95;        // 接地速度(m/s ≈342km/h) これ超は速度超過＝成功にしない
const LANDING_FLARE_MIN_Y = -0.12; // 接地時に機首を下げ過ぎていない判定（前方ベクトルの y がこれ以上）
const APPROACH_DIST_M = 3000;      // この水平距離(m)以内で「○○に接近中」を表示
const APPROACH_EXIT_M = 3500;      // ヒステリシス: これを超えたら接近表示を解除

// --- 世界史スポット（くぐる輪）---
const SITE_APPROACH_M = 12000;      // この水平距離(m)以内で「○○が近い」トースト
const SITE_APPROACH_EXIT_M = 15000; // ヒステリシス
const ONBOARD_DELAY_MS = 4000;      // 初回の遊び方トーストを出すまでの待ち

const _groundRay = new Raycaster();
const _rayOrigin = new Vector3();
const _rayDown = new Vector3(0, -1, 0);

const _fwdRay = new Raycaster();
const _fwdVec = new Vector3();
const FORWARD_BUFFER_M = 25; // keep aircraft this far from any mesh ahead

// 1フレームの移動を線分として輪の通過判定に渡すための始点（毎フレーム使い回す）。
const _prevPos = new Vector3();

// Sea-level Y in the local ENU frame at horizontal distance d from origin.
// (Far from the chosen city the actual surface drops away from the tangent
// plane: y = -d²/(2R).)
// Sea-level Y in the local ENU frame at horizontal distance d from origin.
// The ENU origin sits at `originElevation` metres above sea level, so true
// sea level itself is at `-originElevation` directly under the origin and
// drops further by `-d²/(2R)` due to Earth curvature.
function curvatureY(x, z, originElevation) {
  return -originElevation - (x * x + z * z) / (2 * EARTH_R);
}

// Cast a ray in the aircraft's forward direction; if the path is blocked
// (mountain wall, building face), push the aircraft back so it can't tunnel
// through. The ground raycast alone misses vertical walls because it only
// looks straight down.
function preventForwardCollision(aircraft, tiles) {
  if (!aircraft || !tiles?.group) return;
  _fwdVec.set(0, 0, 1).applyQuaternion(aircraft.quaternion);
  _fwdRay.set(aircraft.position, _fwdVec);
  _fwdRay.far = FORWARD_BUFFER_M;
  const hits = _fwdRay.intersectObject(tiles.group, true);
  if (hits.length === 0) return;
  // Push the aircraft back so it stays FORWARD_BUFFER_M away from the hit.
  const overshoot = FORWARD_BUFFER_M - hits[0].distance;
  if (overshoot > 0) aircraft.position.addScaledVector(_fwdVec, -overshoot);
}

// Clamps the aircraft to the terrain and reports state.
// Returns { onGround, groundY, floor } — used for ground collision, AGL, landing.
// (Land/sea for the speed cap is decided geographically in coastline.js, not here.)
function clampAircraftToGround(aircraft, tiles, originElevation) {
  if (!aircraft) return { onGround: false, groundY: 0, floor: 0 };
  // Floor = curvature-corrected sea level. Without subtracting the spawn
  // elevation, the floor sits at the ENU origin's altitude everywhere,
  // trapping the aircraft hundreds of metres above true sea level when the
  // chosen city is at altitude (e.g. Gotemba 462m).
  const seaLevelY = curvatureY(
    aircraft.position.x,
    aircraft.position.z,
    originElevation
  );
  let groundY = seaLevelY;
  if (tiles?.group) {
    _rayOrigin.set(
      aircraft.position.x,
      aircraft.position.y + 10000,
      aircraft.position.z
    );
    _groundRay.set(_rayOrigin, _rayDown);
    _groundRay.far = 20000;
    const hits = _groundRay.intersectObject(tiles.group, true);
    // Use the highest hit that's BELOW the aircraft. 3D photogrammetry tiles
    // around mountains often contain overhanging geometry above the aircraft
    // (rocky cliffs, drape artifacts). Without this filter, hits[0] would be
    // such overhang and the aircraft would be trapped above it, unable to
    // descend into the valley next to a mountain.
    for (const hit of hits) {
      if (hit.point.y > aircraft.position.y) continue;
      if (hit.point.y > groundY) groundY = hit.point.y;
      break;
    }
  }
  const clearance = aircraft.userData?.bellyOffset ?? 1;
  const floor = groundY + clearance;
  if (aircraft.position.y < floor) {
    aircraft.position.y = floor;
  }
  const onGround = aircraft.position.y <= floor + ON_GROUND_MARGIN;
  return { onGround, groundY, floor };
}

export function startGame({ lat, lng, name, apiKey, elevation = 0, lookAt }) {
  // ENU ⇄ lat/lng の正確な変換フレーム（tiles.js の ReorientationPlugin と同じ
  // 原点・軸規約）。ミニマップ・空港前計算・都市名トラッキングで共用する。
  const enu = createEnuFrame(lat, lng, elevation);
  // 出発都市原点を基準にした各空港の ENU 座標を前計算（着陸/接近判定に使う）。
  const enuAirports = airportsToEnu(enu);
  // 世界史スポットも同じ ENU フレームで前計算する（y は輪の中心の高さ）。
  const enuSites = sitesToEnu(enu);
  // 領海/公海の速度判定に使う海岸線分類器。非同期ロード（数MB, 失敗しても throw せず）。
  // ready になるまで速度上限は既定=領海(900) のままで、海上でも高速化しない。
  const coastline = createCoastlineClassifier();
  coastline.load(import.meta.env.BASE_URL + 'data/land.geojson');
  const canvas = document.getElementById('three-canvas');
  const renderer = new WebGLRenderer({
    canvas,
    antialias: true,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
  renderer.setSize(window.innerWidth, window.innerHeight, false);

  const scene = new Scene();
  scene.background = new Color(0x88aacc);
  scene.fog = new Fog(0x88aacc, 8000, 40000);

  const camera = new PerspectiveCamera(
    60,
    window.innerWidth / window.innerHeight,
    1,
    50000
  );
  camera.position.set(0, SPAWN_ALTITUDE + 8, -25);
  camera.lookAt(new Vector3(0, SPAWN_ALTITUDE, 0));

  // Lighting
  scene.add(new AmbientLight(0xffffff, 0.65));
  const sun = new DirectionalLight(0xffffff, 0.9);
  sun.position.set(1, 1.2, 0.6).normalize().multiplyScalar(2000);
  scene.add(sun);

  // Tiles — real-time Google Photorealistic 3D Tiles (worldwide).
  // height = ENU origin elevation (so y=0 ≒ local terrain).
  // PC は高精細（errorTarget=4 + LRU拡大）、モバイルは負荷を抑える(6)。
  const maxDetail = !isTouchDevice();
  const tiles = createTilesRenderer({
    apiKey, lat, lng, height: elevation, renderer,
    errorTarget: maxDetail ? 4 : 6,
    lruMaxSize: maxDetail ? 5000 : 2500,
  });
  scene.add(tiles.group);

  const hud = createHud({ name });
  hud.setSiteProgress(progress.size, TOTAL_SITES);
  const engineAudio = createEngineAudio();

  // 世界史スポットの輪。scene に直接足す（tiles.group に入れると
  // clampAircraftToGround / preventForwardCollision のレイキャストが拾って、
  // 空中に見えない壁と床ができてしまう）。
  const rings = createHistoricRings({
    scene,
    enuSites,
    isVisited: (id) => progress.has(id),
    showLabels: !isTouchDevice(),
  });
  const siteModal = createSiteModal({
    onResume: () => resumeGame(),
    onAnswer: (id, correct) => progress.setQuiz(id, correct),
  });

  // Minimap is created later, after the mode-select runs and the
  // `.mobile-mode` class has been applied — so the Map instance is built with
  // the correct final container size.
  let minimap = null;
  // Reverse-geocoding tracker: updates the HUD city row to whatever city the
  // aircraft is currently flying over (throttled internally).
  let cityTracker = null;

  // audio-toggle button (top-right). Declared BEFORE inputPromise: that IIFE now
  // runs synchronously (no chooser await) and removes this in mobile mode — a
  // later `let` would throw a TDZ ReferenceError and abort the mobile branch.
  let audioBtn = document.getElementById('audio-toggle');

  let coverageWarned = false;
  tiles.addEventListener?.('load-error', (event) => {
    if (coverageWarned) return;
    const status = event?.error?.status ?? event?.status;
    if (status === 404) {
      hud.showToast('この地域は3D Tilesの対応外、もしくはカバレッジが限定的です');
      coverageWarned = true;
    }
  });

  // Aircraft (async)
  let aircraft = null;
  let controls = null;

  const aircraftPromise = loadAircraft()
    .then((craft) => {
      aircraft = craft;
      aircraft.position.set(0, SPAWN_ALTITUDE, 0);
      // Optional: face a landmark at spawn (lookAt is { lat, lng } in CITIES).
      // ENU world coords: +X = WEST, +Z = NORTH. Rotation around Y so that
      // local forward (+Z) points to the target direction.
      if (lookAt) {
        const p = enu.latLngToWorld(lookAt.lat, lookAt.lng, elevation);
        const heading = Math.atan2(p.x, p.z);
        aircraft.quaternion.setFromAxisAngle(new Vector3(0, 1, 0), heading);
      }
      scene.add(aircraft);
      // スポーン位置がすでに輪の内側なら「入場済み」として記録し、離陸前に
      // モーダルが開かないようにする（出発地＝名所の座標が一致する場合がある）。
      rings.arm(aircraft.position);
      return craft;
    })
    .catch((e) => {
      console.error('aircraft load failed', e);
      hud.showToast('機体モデルの読み込みに失敗しました');
      throw e;
    });

  const inputPromise = (async () => {
    // 端末で自動判定して即スタート（タッチ=スマホUI / それ以外=PCキーボード）。
    // 選択画面は出さない。PCモードのみエンジン音を自動開始（モバイルは無音。
    // iOS で HTMLAudio のループ継ぎ目が出るため）。
    const mode = isTouchDevice() ? 'mobile' : 'pc';
    if (mode === 'pc') engineAudio.start();
    hud.setControlMode(mode);
    if (mode === 'mobile') {
      // Mobile-mode-only layout (e.g., minimap moves to top-right since the
      // throttle buttons take the bottom-right corner).
      document.getElementById('game-root')?.classList.add('mobile-mode');
      // Disable audio entirely for mobile mode.
      if (audioBtn) {
        audioBtn.remove();
        audioBtn = null;
      }
      engineAudio.dispose();
    }

    // Now the mobile-mode class (if any) is applied — the minimap container
    // has its final size, so the Map instance is built correctly.
    createMinimap({ centerLat: lat, centerLng: lng })
      .then((m) => { minimap = m; })
      .catch((e) => console.warn('[minimap] init failed:', e));
    createCityTracker({ apiKey, onCity: (n) => hud.setCity(n) })
      .then((t) => { cityTracker = t; })
      .catch((e) => console.warn('[cityTracker] init failed:', e));

    if (mode === 'mobile') {
      try {
        return await createMobileInputProvider();
      } catch (e) {
        console.warn('mobile input init failed, falling back to keyboard:', e);
        hud.showToast('傾きセンサーが使えないためキーボード操作にフォールバック');
        hideOverlay();
        return createKeyboardInputProvider();
      }
    }
    hideOverlay();
    return createKeyboardInputProvider();
  })();

  Promise.all([aircraftPromise, inputPromise])
    .then(([craft, provider]) => {
      controls = createControls(craft, provider);
    })
    .catch(() => { /* already logged */ });

  const onResize = () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight, false);
  };
  window.addEventListener('resize', onResize);

  const VIEW_PRESETS = {
    chase:   { icon: '👁', ariaLabel: '視点切替（現在: 後方）',     toast: '視点: 後方' },
    cockpit: { icon: '🎯', ariaLabel: '視点切替（現在: コックピット）', toast: '視点: コックピット' },
  };
  let viewMode = 'chase';
  const viewBtn = document.getElementById('view-toggle');
  function syncViewBtn() {
    if (!viewBtn) return;
    const p = VIEW_PRESETS[viewMode];
    viewBtn.textContent = p.icon;
    viewBtn.setAttribute('aria-label', p.ariaLabel);
  }
  function toggleViewMode() {
    viewMode = viewMode === 'cockpit' ? 'chase' : 'cockpit';
    hud.showToast(VIEW_PRESETS[viewMode].toast);
    syncViewBtn();
  }
  // ゲーム中のホットキーは1本のリスナーにまとめる。ポーズ中に効かないという
  // 約束をここ1箇所で守れるので、キーを足すときに個別のガードを書き忘れられない。
  // （ポインタ操作の方は #site-modal が z-index 20 で覆うので構造的に止まる。）
  const onGameKey = (e) => {
    if (paused) return;
    switch (e.code) {
      case 'KeyR': {
        if (!aircraft) return;
        const yaw = rotateModelYaw90(aircraft);
        if (yaw == null) return;
        const deg = Math.round((yaw * 180) / Math.PI);
        hud.showToast(`機体モデル向き: ${deg}° (R キーで90°ずつ回転)`);
        return;
      }
      case 'KeyM':
        engineAudio.setMuted(!engineAudio.isMuted());
        hud.showToast(engineAudio.isMuted() ? 'エンジン音: ミュート' : 'エンジン音: ON');
        syncAudioBtn();
        return;
      case 'KeyV':
        toggleViewMode();
        return;
      default:
    }
  };
  window.addEventListener('keydown', onGameKey);
  const onViewBtn = (e) => {
    e.preventDefault();
    toggleViewMode();
  };
  viewBtn?.addEventListener('pointerdown', onViewBtn);
  syncViewBtn();

  // Audio toggle button — primary user-gesture path for iOS Safari.
  // (audioBtn is declared earlier — set to null in mobile mode where audio is off.)
  function syncAudioBtn() {
    if (!audioBtn) return;
    const started = engineAudio.isStarted();
    const running = started && engineAudio.getState() === 'running';
    const muted = engineAudio.isMuted();
    if (!running) {
      audioBtn.textContent = '🔇';
      audioBtn.classList.add('needs-action');
    } else {
      audioBtn.textContent = muted ? '🔇' : '🔊';
      audioBtn.classList.remove('needs-action');
    }
  }
  const onAudioBtn = (e) => {
    e.preventDefault();
    if (!engineAudio.isStarted()) {
      engineAudio.start();
    } else {
      engineAudio.setMuted(!engineAudio.isMuted());
    }
    syncAudioBtn();
  };
  audioBtn?.addEventListener('pointerdown', onAudioBtn);
  syncAudioBtn();
  // Poll AudioContext state every 500ms to update icon when context transitions
  // from 'suspended' to 'running' asynchronously after start().
  const audioBtnPoll = setInterval(syncAudioBtn, 500);

  const clock = new Clock();
  let stopped = false;
  let paused = false;
  let mutedBeforePause = false;
  let rafId = 0;
  let frameCounter = 0;
  let onGround = false;
  let wasAirborne = false;        // 30m 以上に上がったら true（着陸判定をアーム）
  let lastGroundY = -elevation;   // 直近の地表 Y（AGL 表示用、初期は原点直下の海面相当）
  let approachingName = null;     // 現在「接近中」表示している空港名
  let prevOnGround = false;       // 接地ヒステリシス用（前フレームの接地状態）
  let prevSpeedMs = Infinity;     // Vr コールアウトの立ち上がり検出用（Infinity 初期化で初回誤発火防止）
  const GROUND_STICK_M = 3;       // タイルLOD差し替えで floor が沈んでも接地扱いを維持する高さ(m)
  let approachingSiteId = null;   // 現在「が近い」トーストを出した世界史スポット
  let lastSiteVersion = -1;       // ミニマップへ渡した近傍セットの変化検出用

  // --- ポーズ ---
  // rAF は止めない。物理だけを凍らせて描画とタイルのストリーミングは続けるので、
  // モーダルの背後に3Dシーンが見え、読んでいる間に周囲のタイルも精細になる。
  function pauseGame() {
    if (paused) return;
    paused = true;
    // ユーザーが自分でミュートしていた場合を壊さないよう、状態を退避して復元する。
    mutedBeforePause = engineAudio.isMuted();
    engineAudio.setMuted(true);
    syncAudioBtn();
    controls?.setEnabled(false);
  }

  function resumeGame() {
    if (!paused) return;
    paused = false;
    engineAudio.setMuted(mutedBeforePause);
    syncAudioBtn();
    // 加速キーを押したままモーダルに入っていても、ここで状態がクリアされるので
    // 再開直後に暴走しない。
    controls?.setEnabled(true);
  }

  // 輪をくぐったときの処理。進捗を記録してモーダルを開き、ゲームを止める。
  function enterSite(site) {
    const isNew = progress.mark(site.id);
    if (isNew) {
      rings.markVisited(site.id); // 色替え＋ミニマップ同期用のバージョン更新
      hud.setSiteProgress(progress.size, TOTAL_SITES);
    }
    pauseGame();
    siteModal.open(site, {
      visitedCount: progress.size,
      totalCount: TOTAL_SITES,
      alreadyVisited: !isNew,
    });
  }

  // 初回だけ「輪をくぐると解説が開く」ことを知らせる。
  let onboardTimer = 0;
  if (!progress.isOnboarded()) {
    onboardTimer = setTimeout(() => {
      hud.showToast('空に光る輪＝世界史スポット。くぐると解説が開きます', 6000);
      progress.markOnboarded();
    }, ONBOARD_DELAY_MS);
  }

  function frame() {
    if (stopped) return;
    // ポーズ判定より前に必ず drain する。rAF を回し続けているので、
    // 再開直後の dt は通常どおり ~16ms になり、クロックの補正は要らない。
    const dt = Math.min(0.05, clock.getDelta());

    if (!paused && controls) {
      const maxSpeed = coastline.isHighSeas ? MAX_SPEED_OCEAN_MS : MAX_SPEED_MS;
      const prevY = aircraft.position.y;
      _prevPos.copy(aircraft.position); // 輪の通過判定に使う掃引の始点
      controls.update(dt, { onGround, maxSpeed });
      // 接地降下率は「前方衝突の押し戻し」と「地面クランプのスナップ」より前に測る
      // （= 純粋な空力的上下移動 speed*sin(pitch) になり、衝撃の真値が取れる）。
      const vSpeedRaw = dt > 0 ? (aircraft.position.y - prevY) / dt : 0;
      engineAudio.setSpeed(controls.speed);
      preventForwardCollision(aircraft, tiles);
      const ground = clampAircraftToGround(aircraft, tiles, elevation);
      onGround = ground.onGround;
      // 接地ヒステリシス: 長い滑走中にストリーミングタイルの LOD 差し替えで floor が
      // 0.5m 超沈むと1フレームだけ空中扱いになり、速度が SPEED_MIN_AIR(40) へ跳ぶ。
      // Vr 未満で直前まで接地しており落差が小さければ接地扱いを維持する。
      if (!onGround && prevOnGround && controls.speed < controls.rotateSpeed &&
          aircraft.position.y - ground.floor < GROUND_STICK_M) {
        aircraft.position.y = ground.floor;
        onGround = true;
      }
      lastGroundY = ground.groundY;

      // 離陸グルー: 車輪が地面を離れられるのは「Vr 以上 かつ 機首上げ操作中」のときだけ。
      // - Vr 未満: 機首を上げても飛ばない（加速滑走が必要）
      // - Vr 以上でも操作していなければ接地維持（高速接地後のバルーン再浮上を防ぎ、
      //   ロールアウトが続く。機首上げを保持したまま接地すればタッチ&ゴーになる）
      if (onGround && (controls.speed < controls.rotateSpeed || !controls.noseUpCommanded)) {
        aircraft.position.y = ground.floor;
      }

      // Vr コールアウト: 離陸滑走で地上のまま下から Vr を跨いだ瞬間に一度だけ表示
      // （減速時・スポーン時は発火しない）。
      if (onGround && prevSpeedMs < controls.rotateSpeed && controls.speed >= controls.rotateSpeed) {
        hud.showToast('Vr！機首を上げて離陸');
      }
      prevSpeedMs = controls.speed;
      prevOnGround = onGround;

      // 世界史スポットの輪の通過判定。前方衝突の押し戻しと地面クランプを終えた
      // 「実際に機体がいる位置」を終点にするので、見た目と判定が一致する。
      const hitSite = rings.checkTrigger(_prevPos, aircraft.position);
      if (hitSite) enterSite(hitSite);

      // 着陸判定: 一度 LANDING_AGL_AIRBORNE 以上に上がってから接地した瞬間に評価。
      const agl = aircraft.position.y - ground.groundY;
      if (agl > LANDING_AGL_AIRBORNE) wasAirborne = true;
      if (wasAirborne && onGround) {
        evaluateLanding(aircraft, controls.speed, vSpeedRaw, enuAirports, hud);
        wasAirborne = false;
      }
    }
    if (aircraft) updateChaseCamera(camera, aircraft, dt, viewMode);
    camera.updateMatrixWorld();

    tiles.setCamera(camera);
    tiles.setResolutionFromRenderer(camera, renderer);
    tiles.update();

    // 輪の見た目はポーズ中も更新する（脈動が続くので凍結した画面が生きて見える）。
    if (aircraft) rings.update(dt, aircraft.position, frameCounter);

    if (aircraft && !paused) {
      const heading = extractHeading(aircraft);
      // 最寄り空港（ナビ補助）＋ 3km 圏内に入った瞬間「○○に接近中」トースト。
      // 接近トースト/強調は主要空港（キュレーション or large）に限定し、密に分布する
      // 中規模空港でのトースト濫発を防ぐ（HUD の最寄り行は全空港を表示のまま）。
      const near = nearestAirport(
        enuAirports,
        aircraft.position.x,
        aircraft.position.y,
        aircraft.position.z
      );
      const nearMajor = near && near.airport.size !== 'medium';
      const inRangeName =
        nearMajor && near.distanceM <= APPROACH_DIST_M ? near.airport.name : null;
      if (inRangeName && inRangeName !== approachingName) {
        hud.showToast(`${inRangeName} に接近中`);
        approachingName = inRangeName;
      } else if (!near || near.distanceM > APPROACH_EXIT_M) {
        approachingName = null; // 圏外に出たら解除（別空港への再接近で再表示できる）
      }
      // 最寄りの世界史スポット（ナビ補助）＋ 12km 圏内に入った瞬間のトースト。
      // 空港とまったく同じヒステリシスの作り。
      const nearSite = nearestSite(
        enuSites,
        aircraft.position.x,
        aircraft.position.y,
        aircraft.position.z
      );
      const inRangeSite =
        nearSite && nearSite.distanceM <= SITE_APPROACH_M ? nearSite.site : null;
      if (inRangeSite && inRangeSite.id !== approachingSiteId) {
        hud.showToast(`${inRangeSite.name} が近い — 光る輪をくぐると解説`);
        approachingSiteId = inRangeSite.id;
      } else if (!nearSite || nearSite.distanceM > SITE_APPROACH_EXIT_M) {
        approachingSiteId = null;
      }
      // 高度(MSL)は曲率補正した海面 seaLevelY からの高さ。地面クランプ(clampAircraftToGround)の
      // 床と同じ curvatureY 基準なので、対地(aglM)と参照面が揃い HUD 内で相互整合する。
      const seaLevelY = curvatureY(
        aircraft.position.x,
        aircraft.position.z,
        elevation
      );
      hud.update({
        speedMs: controls?.speed ?? 0,
        altitudeM: aircraft.position.y - seaLevelY,
        headingRad: heading,
        aglM: aircraft.position.y - lastGroundY,
        airport: near && {
          name: near.airport.name,
          distanceM: near.distanceM,
          relBearingRad: near.bearingRad - heading,
          approaching: nearMajor && near.distanceM <= APPROACH_DIST_M,
        },
        site: nearSite && {
          name: nearSite.site.name,
          distanceM: nearSite.distanceM,
          relBearingRad: nearSite.bearingRad - heading,
          visited: progress.has(nearSite.site.id),
        },
      });
      // Throttle minimap / city-tracker updates to ~15Hz to avoid GC churn.
      // worldToLatLng is ellipsoid-exact, so the minimap stays aligned with
      // the 3D tiles even hundreds of km from the origin (the old flat-earth
      // approximation drifted ~10 km on a Tokyo→Osaka flight).
      if ((minimap || cityTracker || coastline.ready) && (frameCounter & 3) === 0) {
        const pos = enu.worldToLatLng(
          aircraft.position.x,
          aircraft.position.y,
          aircraft.position.z
        );
        minimap?.update({ lat: pos.lat, lng: pos.lng, headingRad: heading });
        cityTracker?.maybeUpdate(
          pos.lat, pos.lng,
          aircraft.position.x, aircraft.position.z
        );
        // 領海(≤12海里→900)/公海(高速) 判定。スロットル・ヒステリシスは coastline 側。
        // 陸・川・湖は領海扱い（海抜付近でも高速化しない）＝旧 raycast 方式の不安定を解消。
        coastline.update(pos.lat, pos.lng, aircraft.position.x, aircraft.position.z);
        // 近傍の世界史スポットをミニマップへ。集合が変わったときだけ差し替える
        // （マーカーの生成・破棄は毎フレームやるには重い）。
        const siteVersion = rings.getActiveVersion();
        if (minimap && siteVersion !== lastSiteVersion) {
          lastSiteVersion = siteVersion;
          minimap.setSites(
            rings.getActive().map((s) => ({
              id: s.id, name: s.name, lat: s.lat, lng: s.lng,
              visited: progress.has(s.id),
            }))
          );
        }
      }
    }
    frameCounter++;

    renderer.render(scene, camera);
    rafId = requestAnimationFrame(frame);
  }
  rafId = requestAnimationFrame(frame);

  return {
    // Escape を main.js から委譲される。モーダルが開いていれば閉じて再開し true を
    // 返す（＝タイトルへは戻らない）。もう一度 Escape を押せば都市選択へ戻る。
    // Escape の処理経路はこの1本だけ（siteModal は Enter と ○× キーだけを見る）。
    handleEscape() {
      if (!siteModal.isOpen()) return false;
      siteModal.close(); // → onResume → resumeGame()
      return true;
    },
    dispose() {
      stopped = true;
      cancelAnimationFrame(rafId);
      clearTimeout(onboardTimer);
      clearInterval(audioBtnPoll);
      audioBtn?.removeEventListener('pointerdown', onAudioBtn);
      window.removeEventListener('resize', onResize);
      window.removeEventListener('keydown', onGameKey);
      viewBtn?.removeEventListener('pointerdown', onViewBtn);
      try { minimap?.dispose(); } catch (e) { console.warn(e); }
      try { cityTracker?.dispose(); } catch (e) { console.warn(e); }
      // シーンに初めて自前のメッシュ／テクスチャが入るので、明示的に破棄する。
      try { siteModal.dispose(); } catch (e) { console.warn(e); }
      try { rings.dispose(); } catch (e) { console.warn(e); }
      document.getElementById('game-root')?.classList.remove('mobile-mode');
      engineAudio.dispose();
      controls?.dispose();
      hud.dispose();
      try {
        tiles.dispose();
      } catch (e) {
        console.warn('tiles dispose error', e);
      }
      renderer.dispose();
    },
  };
}

export function stopGame(handle) {
  handle?.dispose();
}

const _fwd = new Vector3();
function extractHeading(aircraft) {
  _fwd.set(0, 0, 1).applyQuaternion(aircraft.quaternion);
  // After ReorientationPlugin: +Z = north, +X = WEST (per its source). To get
  // standard compass bearing (0 = north, +π/2 = east), use east = -fwd.x.
  return Math.atan2(-_fwd.x, _fwd.z);
}

const _fwdProbe = new Vector3();
// 空中→地上の接地遷移時に着陸の良し悪し・空港該当を評価し、HUD に結果を出す。
function evaluateLanding(aircraft, speed, vSpeedRaw, enuAirports, hud) {
  // 位置が NaN 等で降下率が壊れている場合は判定しない（NaN 比較すり抜けによる誤った成功表示を防ぐ）。
  if (!Number.isFinite(vSpeedRaw)) return;
  const descentRate = Math.max(0, -vSpeedRaw); // 降下中は正の値
  _fwdProbe.set(0, 0, 1).applyQuaternion(aircraft.quaternion);
  const noseLevelOk = _fwdProbe.y >= LANDING_FLARE_MIN_Y; // 機首を下げ過ぎていない
  const speedOk = speed <= LANDING_SPD_MAX;
  const heading = extractHeading(aircraft); // 方位計算は extractHeading に集約
  const ap = landingAirport(
    enuAirports,
    aircraft.position.x,
    aircraft.position.z,
    heading
  );

  // 衝撃が大きすぎる接地は、場所に依らず物理的に失敗。
  if (descentRate > LANDING_VS_HARD) {
    hud.showLanding('着陸失敗（衝撃が大きすぎます）', 'fail');
    return;
  }
  // 降下率はやや高い／速すぎる／機首が下がり過ぎ → ハードランディング。
  if (descentRate > LANDING_VS_GOOD || !speedOk || !noseLevelOk) {
    hud.showLanding('ハードランディング', 'warn');
    return;
  }
  // 良好な接地。空港の滑走路（捕捉円＋方位整合）に乗っていれば成功。
  if (ap) {
    hud.showLanding(`着陸成功！ ${ap.name}`, 'success');
  } else {
    // 空港外へのソフト接地は成功にしない。最寄り空港を案内する。
    const near = nearestAirport(
      enuAirports,
      aircraft.position.x,
      aircraft.position.y,
      aircraft.position.z
    );
    const km = near ? (near.distanceM / 1000).toFixed(1) : '?';
    const nm = near ? near.airport.name : '空港';
    hud.showToast(`空港ではありません（最寄り: ${nm} ${km}km）`);
  }
}

